/**
 * FLOW-V5 Б1/Б2 — «вход состоялся»: одна дорога для всех способов входа,
 * которые не уводят браузер со страницы (код из почты, подтверждение другим
 * каналом, Telegram на странице переноса).
 *
 * Раньше каждый из них клал токен и делал `router.push()` — и если цель
 * совпадала с текущим адресом (`/tradein/s/{id}`), переход был пустым:
 * профиль не перечитывался, `profileStore.isAuthenticated` оставался `false`,
 * и страница переноса продолжала звать «войдите», не привязав перенос к
 * аккаунту.
 *
 * Порядок: токен → перечитать профиль (именно он включает `isAuthenticated`,
 * на который смотрят страницы) → переход, только если адрес другой.
 *
 * Профиль перечитывается без ожидания: решение «куда вести» от него не
 * зависит, а страница, на которой человек остаётся, узнает о входе сама —
 * наблюдателем за `isAuthenticated`.
 */
import type { Router } from 'vue-router'

import { useProfileStore } from '@/modules/profile/store/profileStore'
import { consumeTradeInAuthReturn } from '@/modules/vpn/utils/tradeinClaim'

import { resolvePostAuthRoute } from './postAuthRoute'

const TOKEN_STORAGE_KEY = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'

/** Каким способом вошли на странице (FLOW-V5 §5 `auth_done{method}`). */
export type SignInMethod = 'email' | 'telegram'

let lastMethod: SignInMethod | null = null

/**
 * Способ последнего входа без ухода со страницы. Нужен ровно воронке
 * переноса: страница узнаёт о входе по профилю и иначе не знает, чем вошли.
 * `null` — на этой странице не входили (Google и вход в соседней вкладке).
 */
export function lastSignInMethod(): SignInMethod | null {
  return lastMethod
}

/**
 * @param token      ключ сессии; `null` — вход уже оформил стор (токен в
 *                   хранилище, профиль перечитан — `profileStore.register`)
 * @param isCurrent  попытка входа ещё актуальна (OTP-R3): смена почты,
 *                   закрытие окна и unmount отменяют переход
 * @param method     чем вошли — для воронки переноса (`lastSignInMethod`)
 * @returns `true`, если попытка дожила до конца и окно входа можно закрыть
 */
export async function signInDone(
  router: Router,
  token: string | null,
  isCurrent: () => boolean = () => true,
  method: SignInMethod = 'email',
): Promise<boolean> {
  lastMethod = method
  if (token !== null) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
    void useProfileStore().fetchProfile(true)
  }

  const route = router.currentRoute.value
  // Яндекс без почты возвращает на подтверждение email без spa_auth_token.
  // Только этот продолженный вход вправе забрать сохранённый адрес переноса.
  const tradeInReturn = route.path === '/registration' && route.query.yandex_email_required === '1'
    ? consumeTradeInAuthReturn() ?? undefined
    : route.path
  const target = await resolvePostAuthRoute({ tradeInReturn })
  if (!isCurrent()) return false

  // Тот же адрес — переходить некуда: страница уже на месте и узнает о входе
  // по профилю.
  if (router.resolve(target).path !== router.currentRoute.value.path) {
    await router.push(target)
  }

  return isCurrent()
}
